import { Box, Typography } from '@mui/material';
import { WidgetProps } from '@rjsf/utils';
import { colors } from '@src/styles';
import { TStrategyDescriptions } from './schemaEnhancer';

const monoFontFamily = "'JetBrains Mono', monospace";
const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";

export const StrategyCardsWidget = (props: WidgetProps) => {
  const enumOptions = (props.options.enumOptions ?? []) as { label: string; value: string }[];
  const descriptions = (props.options.descriptions ?? {}) as TStrategyDescriptions;

  return (
    <Box
      role="radiogroup"
      aria-label={props.label || 'Estratégia'}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
        gap: 1,
        width: '100%',
      }}
    >
      {enumOptions.map((option) => {
        const isSelected = props.value === option.value;
        const description = descriptions[option.value];
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            className="schema-custom-button"
            role="radio"
            aria-checked={isSelected}
            disabled={props.disabled || props.readonly}
            onClick={() => props.onChange(option.value)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
              minHeight: 56,
              padding: '10px 12px',
              textAlign: 'left',
              cursor: 'pointer',
              borderRadius: '10px',
              border: `1px solid ${isSelected ? colors.primary.yellow : colors.brand.line}`,
              backgroundColor: isSelected ? 'rgba(255, 160, 21, 0.08)' : colors.brand.surface,
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
              '&:hover': { borderColor: isSelected ? colors.primary.yellow : 'rgba(255, 160, 21, 0.5)' },
              '&:focus-visible': { outline: `2px solid ${colors.primary.yellow}`, outlineOffset: '2px' },
              '&:disabled': { opacity: isSelected ? 1 : 0.45, cursor: 'default' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box
                aria-hidden
                sx={{
                  width: 14,
                  height: 14,
                  flexShrink: 0,
                  borderRadius: '999px',
                  border: `1.5px solid ${isSelected ? colors.primary.yellow : colors.brand.textMuted}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isSelected && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '999px', backgroundColor: colors.primary.yellow }} />
                )}
              </Box>
              <Typography
                sx={{
                  fontFamily: monoFontFamily,
                  fontSize: '11.5px',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  color: isSelected ? colors.primary.yellow : colors.brand.textStrong,
                }}
              >
                {option.label}
              </Typography>
            </Box>
            {description && (
              <Typography
                sx={{
                  fontFamily: titleFontFamily,
                  fontSize: '12px',
                  lineHeight: 1.45,
                  color: isSelected ? colors.brand.textStrong : colors.brand.textSecondary,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
